<template>
  <b-modal v-model="dialog.active" size="lg" centered hide-footer no-close-on-backdrop body-class="p-0"
           @hidden="reset">
    <template #modal-title>{{ msg("Add Guest Feature") }}</template>
    <small class="text-warning ml-1 text-center d-block mb-1 mt-1">
      (If this is an Early Check-in or Late Checkout product, check the correct box below)
    </small>
    <b-card-body>
      <b-row>
        <b-col cols="12">
          <b-row>
            <b-col cols="4" align-self="center">
              <b-img :src="feature.imageUri" fluid thumbnail style="max-width: 240px; max-height: 300px"/>
            </b-col>
            <b-col cols="8">
              <b-row>
                <b-col cols="12">
                  <b-form-group>
                    <template #label>
                      {{ msg('Name') }}

                    </template>
                    <b-input v-model="feature.name" placeholder="Name"/>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="msg('Description')">
                    <b-textarea v-model="feature.description" placeholder="Description"/>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col cols="12">
          <b-form-group :label="msg('Image Uri')">
            <b-input v-model="feature.imageUri" placeholder="Image Uri"/>
          </b-form-group>
        </b-col>

        <!-- org-level default price: what guests see and pay when the property
             or building carries no rate override for this product -->
        <b-col cols="6" md="4">
          <b-form-group :label="msg('defaultprice')">
            <b-input v-model.number="feature.defaultRate" type="number" min="0" step="0.01" placeholder="0.00"/>
          </b-form-group>
        </b-col>
        <b-col cols="6" md="4">
          <b-form-group :label="msg('currency')">
            <b-form-select v-model="feature.defaultCurrency" :options="['EUR', 'CHF', 'USD', 'GBP']"/>
          </b-form-group>
        </b-col>

        <b-col cols="12" v-if="allBranches.length > 1">
          <b-form-group :label="msg('Branch Ids')">
            <v-select
                multiple
                :dir="isRTL ? 'rtl' : 'ltr'"
                v-model="feature.branchIds"
                :options="allBranches"
                class="w-100"
                label="name"
                :reduce="(branch) => branch.id"
                :clearable="false"
            />
          </b-form-group>
        </b-col>

        <!-- translations -->
        <b-col cols="12">
          <b-row>
            <b-col cols="6">{{msg('Translations')}}</b-col>
            <b-col cols="6">
              <div class="text-right">
                <b-dropdown class="d-inline-block" size="sm" text="Select Language">
                  <b-dropdown-item-button v-for="lang in allowedLanguages" @click="addTranslation(lang.code)" :key="lang.code">
                    {{lang.name}}
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>
            </b-col>
          </b-row>

          <b-table small striped hover :items="translations" :fields="translationFields">
            <template #cell(language)="data">
              <b-button variant="link" style="padding: 5px" @click="removeTranslation(data.item.language)">
                <i class="fa-solid fa-xmark"></i>
              </b-button>
              {{data.item.language}}
            </template>
            <template #cell(name)="data">
              <b-input v-model="data.item.name" placeholder="Name"/>
            </template>
            <template #cell(description)="data">
              <b-textarea v-model="data.item.description" placeholder="Description"/>
            </template>
          </b-table>
        </b-col>

        <!-- checkboxes -->
        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox v-model="feature.amountAllowed">
              {{ msg('Amount Allowed') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox v-model="feature.autoApprove">
              {{ msg('Auto Approve') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox v-model="feature.dailyPayment">
              {{ msg('Daily Payment') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox v-model="feature.active">
              {{ msg('Active') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox v-model="feature.autocart">
              {{ msg('Automatic To Cart') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox v-model="feature.standard">
              {{ msg('Default') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <!-- NEW: Early Check-in / Late Checkout (mutually exclusive) -->
        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox
                v-model="feature.earlyCheckin"
                class="highlight-checkbox"
                :disabled="feature.lateCheckout">
              {{ msg('Early Check-in') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="3">
          <b-form-group>
            <b-form-checkbox
                v-model="feature.lateCheckout"
                class="highlight-checkbox"
                :disabled="feature.earlyCheckin">
              {{ msg('Late Checkout') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-btn block @click="reset" variant="outline-primary" :disabled="loading">{{ msg('Cancel') }}</b-btn>
        </b-col>
        <b-col cols="6">
          <b-btn block @click="saveFeature" variant="primary">{{ msg('Save') }}</b-btn>
        </b-col>
      </b-row>

    </b-card-body>
  </b-modal>
</template>

<script>

import {mapActions, mapGetters} from "vuex";

const getFeature = () => {
  return {
    name: null,
    description: null,
    icon: null,
    imageUri: null,
    // MUST be declared here: Vue 2 cannot make a property reactive after the
    // object is created, so leaving these out meant typing a default price
    // updated the object but never re-rendered the input — it looked as if the
    // price could not be entered at all.
    defaultRate: null,
    defaultCurrency: 'EUR',
    amountAllowed: false,
    autoApprove: true,
    dailyPayment: false,
    branchIds: [],
    active: true,
    translations: {},
    earlyCheckin: false,
    lateCheckout: false,
  }
}

const allLanguages = [
  {code: 'en', name: 'English'},
  {code: 'de', name: 'German'},
  {code: 'ru', name: 'Russian'}
];

export default {
  name: "GuestFeatureModal",
  data: () => ({
    filter: {},
    page: {amount: 10, currentPage: 1, total: 0},
    dialog: { active: false },
    loading: false,
    feature: getFeature(),
  }),

  computed: {
    ...mapGetters('data', ['getAllBranches']),
    allBranches() {
      return Object.values(this.getAllBranches)
    },
    translations() {
      return Object.values(this.feature?.translations || {});
    },
    allowedLanguages(){
      return allLanguages.filter(lang => this.feature.translations[lang.code] == null);
    },
    translationFields() {
      return [
        { key: 'language', label: this.msg('language') },
        { key: 'name', label: this.msg('Name') },
        { key: 'description', label: this.msg('Description') },
      ]
    }
  },

  watch: {
    // 🟡 MUTUALLY EXCLUSIVE LOGIC
    "feature.earlyCheckin"(val) {
      if (val) this.feature.lateCheckout = false;
    },
    "feature.lateCheckout"(val) {
      if (val) this.feature.earlyCheckin = false;
    }
  },

  methods: {
    ...mapActions('guestFeature', ['insertGuestFeature', 'updateGuestFeature', 'fetchGuestFeatureById']),

    addNewFeature() {
      this.feature = getFeature();
      this.feature.branchIds = this.allBranches.map(branch => branch.id);
      this.dialog.active = true;
    },

    updateFeature(featureId) {
      let $this = this;
      $this.loading = true;
      this.fetchGuestFeatureById(featureId).then(data => {
        $this.feature = {...getFeature(), ...data.GuestFeature};
        $this.dialog.active = true;
      }).finally(() => {
        $this.loading = false;
      })
    },

    addTranslation(lang) {
      if (!this.feature.translations)
        this.feature.translations = {};
      this.feature.translations = {
        ...this.feature.translations,
        [lang]: { name: '', description: '', language: lang }
      };
    },

    removeTranslation(lang) {
      if (!this.feature.translations) return;
      let t = { ...this.feature.translations };
      delete t[lang];
      this.feature.translations = t;
    },

    reset() {
      this.feature = getFeature();
    },

    saveFeature() {
      let $this = this;
      this.loading = true;

      let promise = this.feature.id == null
          ? this.insertGuestFeature(this.feature)
          : this.updateGuestFeature({ id: this.feature.id, request: this.feature });

      promise.then(() => {
        $this.dialog.active = false;
        $this.$emit('updated');
        $this.reset();
      }).finally(() => {
        $this.loading = false;
      });
    }
  }
}
</script>

<style scoped>
/* brighter color */
::v-deep .highlight-checkbox .custom-control-label {
  color: #ffc107 !important;
  font-weight: 600;
}
</style>
